import { useEffect, useRef } from 'react';
import {
  Search, X, Receipt, Undo2, RefreshCcw, Coins, Wrench, Scale, Wallet
} from 'lucide-react';
import { getSaleDisplayAmount, formatPaymentMethod } from '../utils/saleDisplay';
import { API_HOST } from '../config/api';
import './FislerMobileHome.css';

// iOS WebView Fişler ana görünümü (/mobile/fisler → RetailSales mobileApp modu).
// Liste, sayılar ve filtreler RetailSales'in "Tüm Satış Fişleri" verisinden gelir (/all-receipts,
// sunucu taraflı sayfalama) — web ile birebir aynı; burada yalnızca telefon yerleşimi var.
// Kayda dokununca web'deki ilgili detay modalı açılır (satış, iade/değişim, tahsilat, hurda, masraf).

const TYPE_FILTERS = [
  { key: 'all', label: 'Tümü' },
  { key: 'sale', label: 'Satış' },
  { key: 'refund', label: 'İade' },
  { key: 'debt_payment', label: 'Tahsilat' },
  { key: 'repair', label: 'Tamirat' },
  { key: 'scrap_purchase', label: 'Hurda' },
  { key: 'expense', label: 'Masraf' },
];

const DATE_FILTERS = [
  { key: 'all', label: 'Tümü' },
  { key: 'today', label: 'Bugün' },
  { key: 'yesterday', label: 'Dün' },
  { key: 'week', label: '7 Gün' },
  { key: 'month', label: '30 Gün' },
];

const STORE_FILTERS = [
  { key: 'all', label: 'Tümü', countKey: 'all' },
  { key: '1', label: 'Akik Gold', countKey: 'store1' },
  { key: '2', label: 'Osmangazi', countKey: 'store2' },
  { key: '3', label: 'Yeşil Su', countKey: 'store3' },
];

const STORE_SHORT = { 1: 'Akik Gold', 2: 'Osmangazi', 3: 'Yeşil Su' };

const money = (n) => Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Kayıt türüne göre ikon, renk ve listede gösterilecek tutar (web listesiyle aynı kurallar)
const describeRecord = (sale) => {
  const type = sale.record_type;
  if (type === 'expense') {
    return { tone: 'red', icon: <Wallet size={18} />, badge: 'Masraf', amount: `−₺${money(sale.total)}`, amountTone: 'red' };
  }
  if (type === 'scrap_purchase') {
    const metal = sale.metal_type === 'silver' ? 'Gümüş' : sale.metal_type?.includes(',') ? 'Altın/Gümüş' : 'Altın';
    return { tone: 'amber', icon: <Scale size={18} />, badge: `Hurda ${metal}${sale.item_count > 1 ? ` (${sale.item_count})` : ''}`, amount: `₺${money(sale.total)}`, amountTone: 'amber' };
  }
  if (type === 'debt_payment') {
    const isGram = sale.debt_type === 'GRAM' || sale.debt_type === 'GUMUS';
    return { tone: 'green', icon: <Coins size={18} />, badge: 'Tahsilat', amount: isGram ? `${Number(sale.total).toLocaleString('tr-TR')} gr` : `₺${money(sale.total)}`, amountTone: 'green' };
  }
  if (type === 'repair_payment' || type === 'repair_deposit') {
    return { tone: 'teal', icon: <Wrench size={18} />, badge: 'Tamirat', amount: `₺${money(sale.total)}`, amountTone: 'green' };
  }
  if (type === 'refund' || type === 'exchange') {
    const total = Number(sale.total);
    return {
      tone: type === 'refund' ? 'red' : 'orange',
      icon: type === 'refund' ? <Undo2 size={18} /> : <RefreshCcw size={18} />,
      badge: type === 'refund' ? 'İade' : 'Değişim',
      amount: `${total < 0 ? '−' : '+'}₺${money(Math.abs(total))}`,
      amountTone: total < 0 ? 'red' : 'ink',
    };
  }
  return { tone: 'blue', icon: <Receipt size={18} />, badge: null, amount: `₺${money(getSaleDisplayAmount(sale))}`, amountTone: 'ink' };
};

// "Bugün", "Dün" ya da "15 Ekim Çarşamba" — liste gün başlıklarıyla gruplanır
const dayLabel = (date) => {
  const d = new Date(date);
  const today = new Date();
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((startOf(today) - startOf(d)) / 864e5);
  if (diff === 0) return 'Bugün';
  if (diff === 1) return 'Dün';
  return d.toLocaleDateString('tr-TR', {
    day: 'numeric', month: 'long', weekday: 'long',
    ...(d.getFullYear() !== today.getFullYear() ? { year: 'numeric' } : {})
  });
};

const FislerMobileHome = ({
  isAdmin,
  receipts, counts, loading, loadingMore, hasMore, onLoadMore,
  search, setSearch,
  typeFilter, setTypeFilter,
  dateFilter, setDateFilter,
  storeFilter, setStoreFilter,
  onOpenRecord,
}) => {
  const sentinelRef = useRef(null);

  // Listenin sonuna yaklaşınca sıradaki sayfa yüklenir
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) onLoadMore();
    }, { rootMargin: '400px 0px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore, receipts.length]);

  const hasActiveFilter = search || typeFilter !== 'all' || dateFilter !== 'all';

  // Gün gruplarına böl (liste zaten tarihe göre yeniden eskiye geliyor)
  const groups = [];
  receipts.forEach(sale => {
    const label = dayLabel(sale.created_at);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(sale);
    else groups.push({ label, items: [sale] });
  });

  return (
    <div className="fmh">
      {/* Arama */}
      <div className="fmh-search">
        <Search size={17} />
        <input
          type="search"
          inputMode="search"
          placeholder="Fiş no, müşteri, telefon, personel"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button type="button" className="fmh-search-clear" onClick={() => setSearch('')} aria-label="Temizle">
            <X size={14} />
          </button>
        )}
      </div>

      {/* Mağaza (yalnızca admin) — sayılar seçili tür/tarih/arama için toplam fiş */}
      {isAdmin && (
        <div className="fmh-stores">
          {STORE_FILTERS.map(s => (
            <button
              key={s.key}
              type="button"
              className={`fmh-store${storeFilter === s.key ? ' is-active' : ''}`}
              onClick={() => setStoreFilter(s.key)}
            >
              <span className="fmh-store-name">{s.label}</span>
              <span className="fmh-store-count">{(counts?.[s.countKey] ?? 0).toLocaleString('tr-TR')}</span>
            </button>
          ))}
        </div>
      )}

      {/* Tür */}
      <div className="fmh-chips">
        {TYPE_FILTERS.map(t => (
          <button
            key={t.key}
            type="button"
            className={`fmh-chip${typeFilter === t.key ? ' is-active' : ''}`}
            onClick={() => setTypeFilter(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tarih */}
      <div className="fmh-segment">
        {DATE_FILTERS.map(d => (
          <button
            key={d.key}
            type="button"
            className={dateFilter === d.key ? 'is-active' : ''}
            onClick={() => setDateFilter(d.key)}
          >
            {d.label}
          </button>
        ))}
      </div>

      {hasActiveFilter && (
        <button
          type="button"
          className="fmh-clear"
          onClick={() => { setSearch(''); setTypeFilter('all'); setDateFilter('all'); }}
        >
          Filtreleri temizle
        </button>
      )}

      {loading && receipts.length === 0 ? (
        <div className="fmh-group">
          <div className="fmh-card">
            {[0, 1, 2, 3, 4].map(i => <div key={i} className="fmh-row fmh-skeleton" />)}
          </div>
        </div>
      ) : receipts.length === 0 ? (
        <div className="fmh-empty">
          <div className="fmh-empty-icon">🧾</div>
          <div className="fmh-empty-title">Fiş bulunamadı</div>
          <div className="fmh-empty-text">Bu filtreye uygun kayıt yok.</div>
        </div>
      ) : (
        groups.map((group, idx) => (
          <div key={`${group.label}-${idx}`} className="fmh-group">
            <div className="fmh-day">{group.label}</div>
            <div className="fmh-card">
              {group.items.map(sale => {
                const info = describeRecord(sale);
                const isExpense = sale.record_type === 'expense';
                const title = isExpense ? (sale.description || 'Masraf') : (sale.customer_name || 'Müşteri yok');
                const time = new Date(sale.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
                const method = isExpense ? sale.category : formatPaymentMethod(sale);
                // Satış fişlerinde ikon yerine satılan ilk ürünün fotoğrafı (varsa); yanında ürün sayısı
                const isSale = !sale.record_type || sale.record_type === 'sale';
                const soldItems = isSale && Array.isArray(sale.items) ? sale.items.filter(i => Number(i.quantity) > 0) : [];
                const productImage = soldItems.find(i => i.image)?.image;
                return (
                  <button
                    key={`${sale.record_type}_${sale.id}`}
                    type="button"
                    className="fmh-row"
                    onClick={() => onOpenRecord(sale)}
                  >
                    {productImage ? (
                      <span className="fmh-thumb">
                        <img src={productImage.startsWith('http') ? productImage : `${API_HOST}${productImage}`} alt="" loading="lazy" />
                        {soldItems.length > 1 && <span className="fmh-thumb-count">{soldItems.length}</span>}
                      </span>
                    ) : (
                      <span className={`fmh-icon tone-${info.tone}`}>{info.icon}</span>
                    )}
                    <span className="fmh-main">
                      <span className="fmh-title">{title}</span>
                      <span className="fmh-sub">
                        <span className="fmh-no">#{sale.receipt_number || sale.store_sale_number || sale.id}</span>
                        <span>· {time}</span>
                        {sale.staff_name && <span>· {sale.staff_name}</span>}
                      </span>
                      {(info.badge || (isAdmin && sale.store_id) || (isAdmin && sale.has_no_discount_item)) && (
                        <span className="fmh-tags">
                          {info.badge && <span className={`fmh-tag tone-${info.tone}`}>{info.badge}</span>}
                          {isAdmin && sale.store_id && <span className="fmh-tag tone-gray">{STORE_SHORT[sale.store_id] || 'Mağaza'}</span>}
                          {isAdmin && sale.has_no_discount_item && !info.badge && <span className="fmh-tag tone-red">İndirim kısıtlı ürün</span>}
                        </span>
                      )}
                    </span>
                    <span className="fmh-side">
                      <span className={`fmh-amount tone-text-${info.amountTone}`}>{info.amount}</span>
                      {method && method !== '-' && <span className="fmh-method">{method}</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))
      )}

      {receipts.length > 0 && (
        <div ref={sentinelRef} className="fmh-foot">
          {loadingMore ? 'Yükleniyor…'
            : hasMore ? <button type="button" className="fmh-more" onClick={onLoadMore}>Daha fazla yükle</button>
              : `Tüm fişler yüklendi (${receipts.length})`}
        </div>
      )}
    </div>
  );
};

export default FislerMobileHome;
